import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import { uploadDocumentoCliente, validarArquivoDocumento } from '../services/documentosClienteApi'
import { sincronizarEventoGoogleCalendar } from '../services/googleCalendarApi'

/** Administrativo vinculado a esta empresa pode escolher a empresa da OS (como o administrador). */
const EMPRESA_ID_PODE_ESCOLHER_EMPRESA_NOVA_OS = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

/** Tipos de anexo permitidos ao criar a Ordem de Serviço (mesma tabela do cadastro do cliente). */
const TIPOS_ANEXO_OS = [
  { value: 'unifilar', label: 'Unifilar' },
  { value: 'analise_sombreamento', label: 'Análise de Sombreamento' },
  { value: 'vistoria', label: 'Vistoria' }
]

/**
 * Modal "Nova Ordem de Serviço" para ser usado na Gestão de Clientes.
 * Recebe o cliente pré-selecionado (initialCliente) e permite preencher tipo, equipe, data etc.
 */
const NovaOrdemServicoModal = ({ open, onClose, initialCliente, userRole, userEmpresa, userId, onSaved }) => {
  const isAdmin = userRole === 'administrador'
  const podeEscolherEmpresa =
    isAdmin ||
    (userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_PODE_ESCOLHER_EMPRESA_NOVA_OS)
  const [empresas, setEmpresas] = useState([])
  const [empresaId, setEmpresaId] = useState('')
  const [tiposServico, setTiposServico] = useState([])
  const [equipes, setEquipes] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [message, setMessage] = useState({ text: '', type: '' })

  // Anexos opcionais (Unifilar / Análise de Sombreamento / Vistoria)
  const [anexos, setAnexos] = useState([])
  const [anexoTipo, setAnexoTipo] = useState(TIPOS_ANEXO_OS[0].value)

  const [formData, setFormData] = useState({
    id_tipo_servico: '',
    id_equipe: '',
    status: 'Novo',
    obs_operacional: '',
    data_hora_execucao: (() => {
      const d = new Date()
      const date = d.toISOString().split('T')[0]
      return `${date}T08:00`
    })(),
    presenca: false,
    responsaveis: []
  })

  useEffect(() => {
    if (!open) return
    const idEmpresaCliente = initialCliente?.empresa_id || userEmpresa?.id || ''
    setEmpresaId(idEmpresaCliente)
    if (podeEscolherEmpresa) {
      supabase.from('empresa').select('id, empresa').order('empresa').then(({ data, error }) => {
        if (!error && data) setEmpresas(data)
      })
      return
    }
    if (idEmpresaCliente) {
      supabase.from('empresa').select('id, empresa').eq('id', idEmpresaCliente).maybeSingle().then(({ data, error }) => {
        if (!error && data) setEmpresas([data])
        else if (userEmpresa?.id === idEmpresaCliente && userEmpresa?.empresa) {
          setEmpresas([{ id: userEmpresa.id, empresa: userEmpresa.empresa }])
        }
      })
    }
  }, [open, podeEscolherEmpresa, userEmpresa?.id, userEmpresa?.empresa, initialCliente?.empresa_id])

  useEffect(() => {
    if (!open) return
    const effectiveEmpresaId = empresaId || userEmpresa?.id || (typeof userEmpresa === 'string' ? userEmpresa : null)
    if (!effectiveEmpresaId) {
      setLoadingData(false)
      return
    }
    setLoadingData(true)
    const load = async () => {
      try {
        const [tiposRes, equipesRes, usuariosRes] = await Promise.all([
          supabase.from('tipo_servico').select('id, nome').eq('ativo', true).order('nome'),
          supabase
            .from('equipes')
            .select('id, nome')
            .eq('ativo', true)
            .or(`empresa_id.eq.${effectiveEmpresaId},multiequipes.eq.true`)
            .order('nome'),
          (async () => {
            const { data: dataUsuarios, error: errorUsuarios } = await supabase
              .from('usuarios_sistema')
              .select('id, nome')
              .eq('status', 'ativo')
              .or(`empresa_id.eq.${effectiveEmpresaId},multiempresas.eq.true`)
              .order('nome')
            if (errorUsuarios) throw errorUsuarios
            return dataUsuarios || []
          })()
        ])
        setTiposServico(tiposRes.data || [])
        setEquipes(equipesRes.data || [])
        setUsuarios(await usuariosRes)
      } catch (err) {
        console.error(err)
        setMessage({ text: 'Erro ao carregar dados: ' + (err.message || err), type: 'error' })
      } finally {
        setLoadingData(false)
      }
    }
    load()
  }, [open, empresaId, userEmpresa?.id])

  useEffect(() => {
    if (open) {
      setFormData(prev => ({
        ...prev,
        data_hora_execucao: (() => {
          const d = new Date()
          return `${d.toISOString().split('T')[0]}T08:00`
        })()
      }))
      setMessage({ text: '', type: '' })
      setAnexos([])
      setAnexoTipo(TIPOS_ANEXO_OS[0].value)
    }
  }, [open])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue }
      if (name === 'id_tipo_servico') {
        const tipo = tiposServico.find(t => t.id === value)
        if (tipo) {
          const n = (tipo.nome || '').toLowerCase()
          if (n.includes('instalação') || n.includes('instalacao')) updated.presenca = true
          else if (n.includes('vistoria')) updated.presenca = false
        }
      }
      return updated
    })
  }

  const handleResponsaveisChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, opt => opt.value)
    setFormData(prev => ({ ...prev, responsaveis: selected }))
  }

  const handleAnexoFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const validacao = validarArquivoDocumento(file)
    if (!validacao.ok) {
      setMessage({ text: validacao.error, type: 'error' })
      e.target.value = ''
      return
    }
    const tipoLabel = TIPOS_ANEXO_OS.find(t => t.value === anexoTipo)?.label || anexoTipo
    setAnexos(prev => [...prev, { id: `${Date.now()}_${prev.length}`, tipo: anexoTipo, tipoLabel, file }])
    setMessage({ text: '', type: '' })
    e.target.value = ''
  }

  const handleRemoverAnexo = (id) => {
    setAnexos(prev => prev.filter(a => a.id !== id))
  }

  const verificarConflitoDataHora = async () => {
    if (!formData.data_hora_execucao || !formData.id_equipe) return false
    const dataHora = new Date(formData.data_hora_execucao)
    const start = new Date(dataHora)
    start.setSeconds(0, 0)
    const end = new Date(dataHora)
    end.setSeconds(59, 999)
    const { data: eventos, error } = await supabase
      .from('eventos_calendario')
      .select('id, id_ordem_servico')
      .not('id_ordem_servico', 'is', null)
      .gte('data_hora', start.toISOString())
      .lte('data_hora', end.toISOString())
    if (error) throw error
    if (!eventos?.length) return false
    const ordemIds = [...new Set(eventos.map(e => e.id_ordem_servico).filter(Boolean))]
    const { data: ordensMesmaEquipe } = await supabase
      .from('ordens_servicos')
      .select('id')
      .in('id', ordemIds)
      .eq('id_equipe', formData.id_equipe)
    return (ordensMesmaEquipe && ordensMesmaEquipe.length > 0)
  }

  const verificarConflitoInstalacaoEquipe = async () => {
    const tipo = tiposServico.find(t => t.id === formData.id_tipo_servico)
    if (!tipo) return false
    const n = (tipo.nome || '').toLowerCase()
    if (!n.includes('instalação') && !n.includes('instalacao')) return false
    if (!formData.id_equipe) return false
    const dataExecucao = formData.data_hora_execucao ? formData.data_hora_execucao.split('T')[0] : null
    if (!dataExecucao) return false
    const { data, error } = await supabase
      .from('ordens_servicos')
      .select('id')
      .eq('id_tipo_servico', formData.id_tipo_servico)
      .eq('id_equipe', formData.id_equipe)
      .eq('data_execucao', dataExecucao)
      .in('status', ['Novo', 'Em andamento', 'Validado'])
    if (error) throw error
    return (data && data.length > 0)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!initialCliente?.id) {
      setMessage({ text: 'Cliente não informado.', type: 'error' })
      return
    }
    if (!formData.data_hora_execucao) {
      setMessage({ text: 'Data e hora de execução são obrigatórios.', type: 'error' })
      return
    }
    setLoading(true)
    setMessage({ text: '', type: '' })
    try {
      const conflito = await verificarConflitoDataHora()
      if (conflito) {
        setMessage({ text: 'Já existe uma ordem agendada para a mesma equipe neste horário.', type: 'error' })
        setLoading(false)
        return
      }
      const conflitoInst = await verificarConflitoInstalacaoEquipe()
      if (conflitoInst) {
        setMessage({ text: 'Já existe uma ordem de Instalação para esta equipe nesta data.', type: 'error' })
        setLoading(false)
        return
      }

      const dataExecucao = formData.data_hora_execucao.split('T')[0]
      const effectiveEmpresaId = empresaId || userEmpresa?.id || (typeof userEmpresa === 'string' ? userEmpresa : null)
      const payloadOrdem = {
        id_cliente: initialCliente.id,
        id_tipo_servico: formData.id_tipo_servico,
        id_equipe: formData.id_equipe || null,
        id_empresa: effectiveEmpresaId,
        status: formData.status,
        obs_operacional: formData.obs_operacional || null,
        data_execucao: dataExecucao,
        presenca: formData.presenca
      }
      const { data: ordemData, error: ordemError } = await supabase
        .from('ordens_servicos')
        .insert([payloadOrdem])
        .select()

      if (ordemError) throw ordemError

      if (formData.responsaveis?.length > 0) {
        const responsaveisData = formData.responsaveis.map(id_responsavel => ({
          id_ordem_servico: ordemData[0].id,
          id_responsavel
        }))
        const { error: respErr } = await supabase.from('ordem_servico_responsaveis').insert(responsaveisData)
        if (respErr) throw respErr
      }

      if (userId && formData.data_hora_execucao) {
        try {
          const clienteNome = initialCliente.nome_completo || 'Cliente'
          const tipoNome = tiposServico.find(t => t.id === formData.id_tipo_servico)?.nome || 'Serviço'
          const nomeEvento = `OS #${ordemData[0].id} - ${clienteNome} (${tipoNome})`
          const dataHora = new Date(formData.data_hora_execucao).toISOString()
          const payloadEvento = {
            nome_evento: nomeEvento,
            tipo_evento: 'operacional',
            executado: formData.status === 'Finalizado',
            data_hora: dataHora,
            id_responsavel: userId,
            observacao: `Criada a partir da Ordem de Serviço #${ordemData[0].id}`,
            id_ordem_servico: ordemData[0].id
          }
          const { data: eventoData, error: eventoError } = await supabase
            .from('eventos_calendario')
            .insert([payloadEvento])
            .select('id')
          if (!eventoError && eventoData?.[0]?.id && formData.responsaveis?.length > 0) {
            const usuariosRows = formData.responsaveis.map(usuario_id => ({
              evento_id: eventoData[0].id,
              usuario_id
            }))
            await supabase.from('eventos_calendario_usuarios').insert(usuariosRows)
          }
          if (eventoData?.[0]?.id) {
            await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: eventoData[0].id, op: 'upsert' })
          }
        } catch (evErr) {
          console.warn('Evento no calendário não criado:', evErr)
        }
      }

      // Upload dos anexos (mesmo método do cadastro do cliente): vinculados ao cliente da OS
      let falhasAnexos = 0
      if (anexos.length > 0) {
        for (const anexo of anexos) {
          try {
            await uploadDocumentoCliente({
              clienteId: initialCliente.id,
              tipoDocumento: anexo.tipo,
              arquivo: anexo.file,
              observacao: `Anexado na criação da OS #${ordemData[0].id}`,
              enviadoPor: userId ? String(userId) : 'Sistema'
            })
          } catch (anexoErr) {
            console.error('Erro ao enviar anexo da OS:', anexoErr)
            falhasAnexos += 1
          }
        }
      }

      if (falhasAnexos > 0) {
        setMessage({
          text: `Ordem de serviço criada, mas ${falhasAnexos} anexo(s) não puderam ser enviados.`,
          type: 'error'
        })
      } else {
        setMessage({ text: 'Ordem de serviço criada com sucesso!', type: 'success' })
      }
      if (typeof onSaved === 'function') onSaved()
      setTimeout(() => {
        onClose()
      }, falhasAnexos > 0 ? 1500 : 800)
    } catch (error) {
      console.error(error)
      setMessage({ text: 'Erro ao salvar: ' + (error.message || error), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        width: '100vw',
        height: '100vh',
        padding: '20px',
        boxSizing: 'border-box'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '24px 30px',
          borderRadius: '16px 16px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Nova Ordem de Serviço</h3>
            <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
              Cliente: {initialCliente?.nome_completo || '–'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Fechar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '30px' }}>
          {message.text && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              borderRadius: '8px',
              background: message.type === 'error' ? '#fee2e2' : '#d1fae5',
              color: message.type === 'error' ? '#b91c1c' : '#065f46'
            }}>
              {message.text}
            </div>
          )}

          {loadingData ? (
            <p>Carregando...</p>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Empresa</label>
                <select
                  value={empresaId || ''}
                  onChange={(e) => podeEscolherEmpresa && setEmpresaId(e.target.value)}
                  disabled={!podeEscolherEmpresa}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    cursor: podeEscolherEmpresa ? 'pointer' : 'not-allowed',
                    opacity: podeEscolherEmpresa ? 1 : 0.9
                  }}
                >
                  {!empresaId && <option value="">Selecione a empresa...</option>}
                  {(empresas.length > 0
                    ? empresas
                    : (empresaId && userEmpresa?.id === empresaId && userEmpresa
                      ? [{ id: userEmpresa.id, empresa: userEmpresa.empresa }]
                      : [])
                  ).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.empresa}</option>
                  ))}
                </select>
                {!podeEscolherEmpresa && (
                  <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                    Empresa do cadastro do cliente
                  </small>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Tipo de Serviço *</label>
                <select
                  name="id_tipo_servico"
                  value={formData.id_tipo_servico}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                >
                  <option value="">Selecione um tipo de serviço</option>
                  {tiposServico.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Equipe *</label>
                <select
                  name="id_equipe"
                  value={formData.id_equipe}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                >
                  <option value="">Selecione uma equipe</option>
                  {equipes.map(eq => (
                    <option key={eq.id} value={eq.id}>{eq.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                >
                  <option value="Novo">Novo</option>
                  <option value="Em andamento">Em andamento</option>
                  <option value="Finalizado">Finalizado</option>
                  <option value="Validado">Validado</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Data e Hora de Execução *</label>
                <input
                  type="datetime-local"
                  name="data_hora_execucao"
                  value={formData.data_hora_execucao}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '12px',
                  padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px',
                  backgroundColor: formData.presenca ? '#ecfdf5' : 'white'
                }}>
                  <input
                    type="checkbox"
                    name="presenca"
                    checked={formData.presenca}
                    onChange={handleInputChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#667eea' }}
                  />
                  <span style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>Presença</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Observações Operacionais</label>
                <textarea
                  name="obs_operacional"
                  value={formData.obs_operacional}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Observações para a equipe operacional..."
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical', minHeight: '80px' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  Anexos (opcional)
                </label>
                <small style={{ display: 'block', marginBottom: '10px', color: '#6b7280', fontSize: '12px' }}>
                  Unifilar, Análise de Sombreamento ou Vistoria. Os arquivos ficam disponíveis também no cadastro do cliente. (PDF, JPG, PNG, WebP - máx. 20MB)
                </small>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={anexoTipo}
                    onChange={(e) => setAnexoTipo(e.target.value)}
                    style={{ flex: '1 1 200px', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    {TIPOS_ANEXO_OS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <input
                    type="file"
                    onChange={handleAnexoFileChange}
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    style={{ flex: '1 1 240px', padding: '8px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>

                {anexos.length > 0 && (
                  <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {anexos.map(anexo => (
                      <div
                        key={anexo.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                          padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px'
                        }}
                      >
                        <span style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{anexo.tipoLabel}:</strong> {anexo.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoverAnexo(anexo.id)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
                          title="Remover anexo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>Responsáveis</label>
                <select
                  name="responsaveis"
                  multiple
                  value={formData.responsaveis}
                  onChange={handleResponsaveisChange}
                  style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box', minHeight: '120px' }}
                >
                  {usuarios.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Pressione Ctrl (ou Cmd no Mac) para selecionar múltiplos
                </small>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" onClick={onClose} style={{ padding: '12px 24px', border: '2px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '500', backgroundColor: 'white', color: '#374151' }}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.id_tipo_servico || !formData.id_equipe}
                  style={{
                    padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '16px', fontWeight: '500',
                    background: loading || !formData.id_tipo_servico || !formData.id_equipe ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white', opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Salvando...' : 'Criar Ordem de Serviço'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null
}

export default NovaOrdemServicoModal
